import { useNavigate } from 'react-router-dom';
import { Building2, Palmtree, TrendingUp, ArrowRight, Sparkles, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackEvent } from '@/lib/analytics';

interface ExploreCard {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  image: string;
  href: string;
  gradient: string;
  id: string;
}

interface ExploreMoreSectionProps {
  onContactClick: () => void;
  currentPage?: 'residences' | 'amenities' | 'invest' | 'commercial';
}

const allCards: ExploreCard[] = [
  {
    id: 'residences',
    title: 'Luxury Residences',
    description: 'Discover your perfect home among our collection of exceptional waterfront properties',
    icon: Home,
    image: '/img/residence-interior.jpg',
    href: '/residences',
    gradient: 'from-brand-navy/20 to-brand-teal/20',
  },
  {
    id: 'invest',
    title: 'Investment Opportunities',
    description: 'Discover exceptional returns in one of the Caribbean\'s most sought-after addresses',
    icon: TrendingUp,
    image: '/img/aerial-1.png',
    href: '/invest',
    gradient: 'from-brand-teal/20 to-brand-navy/20',
  },
  {
    id: 'amenities',
    title: 'World-Class Amenities',
    description: 'Experience resort-style living with our curated collection of premium facilities',
    icon: Palmtree,
    image: '/img/lagoon-amenity.jpg',
    href: '/amenities',
    gradient: 'from-brand-sky-blue/20 to-brand-teal/20',
  },
  {
    id: 'commercial',
    title: 'Commercial Spaces',
    description: 'Prime retail and office opportunities in a thriving waterfront destination',
    icon: Building2,
    image: '/img/food-hall.jpg',
    href: '/commercial',
    gradient: 'from-brand-navy/20 to-brand-teal/20',
  },
];

export const ExploreMoreSection = ({ onContactClick, currentPage }: ExploreMoreSectionProps) => {
  const navigate = useNavigate();

  // Filter out the current page and select 3 cards
  const exploreCards = allCards
    .filter(card => card.id !== currentPage)
    .slice(0, 3);

  const handleCardClick = (card: ExploreCard) => {
    trackEvent('internal_link_click', { 
      from: 'residences_page', 
      to: card.href,
      title: card.title 
    });
    navigate(card.href);
  };

  return (
    <section className="relative py-24 bg-gradient-to-b from-soft-cream to-pearl-white overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-brand-teal/5 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-deep-navy/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-teal/10 border border-brand-teal/20 mb-6">
            <Sparkles className="w-4 h-4 text-brand-teal" />
            <span className="text-sm font-medium text-brand-teal uppercase tracking-wider">
              Discover More
            </span>
          </div>
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Explore The Pearl Experience
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Beyond exceptional residences, discover a world of possibilities at Grand Harbour's premier waterfront community
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {exploreCards.map((card, index) => {
            const Icon = card.icon;
            return (
              <div
                key={card.href}
                className="group relative bg-card rounded-2xl overflow-hidden border border-border hover:border-brand-teal/50 transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 cursor-pointer"
                onClick={() => handleCardClick(card)}
                style={{
                  animationDelay: `${index * 100}ms`,
                }}
              >
                {/* Image Background */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={card.image}
                    alt={card.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${card.gradient} to-transparent`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                  
                  {/* Icon Badge */}
                  <div className="absolute top-6 left-6 w-12 h-12 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-brand-teal" />
                  </div>
                </div>

                {/* Content */}
                <div className="p-8">
                  <h3 className="font-display text-2xl font-bold text-foreground mb-3 group-hover:text-brand-teal transition-colors duration-300">
                    {card.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    {card.description}
                  </p>
                  
                  <Button
                    variant="ghost"
                    className="group/btn p-0 h-auto text-brand-teal hover:text-brand-teal/80 hover:bg-transparent font-semibold"
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover/btn:translate-x-1" />
                  </Button>
                </div>

                {/* Hover Glow Effect */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                  <div className="absolute inset-0 bg-gradient-to-r from-brand-teal/5 via-transparent to-brand-teal/5" />
                </div>
              </div>
            );
          })}
        </div>

        {/* Additional CTA */}
        <div className="mt-16 text-center">
          <p className="text-muted-foreground mb-4">
            Have questions? Our team is here to help
          </p>
          <Button
            size="lg"
            variant="outline"
            className="border-brand-teal text-brand-teal hover:bg-brand-teal hover:text-white"
            onClick={() => {
              trackEvent('cta_click', { from: 'explore_more_section', action: 'contact_team' });
              onContactClick();
            }}
          >
            Contact Our Team
          </Button>
        </div>
      </div>
    </section>
  );
};
